import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import {
  EvictionReason,
  ForageKind,
  ForagePlant as ForagePlantData,
  ForageSkill,
  ForageState,
  FrenzyStatus,
  HarvestErrorReason,
  joinForage,
} from "../api/forage";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { ForagePlant } from "../components/ForagePlant";
import {
  ForageHarvestGhost,
  ForageHarvestGhostImg,
  HARVEST_POP_MS,
} from "../components/ForageHarvestGhost";
import {
  ForageFloatingXp,
  ForageFloatingXpLabel,
  FLOAT_DURATION_MS,
} from "../components/ForageFloatingXp";
import { ForageSidePanel } from "../components/ForageSidePanel";
import { ActivityEvictionPage } from "../components/ActivityEvictionPage";
import { KIND_XP } from "../components/forageSprites";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { playSfx } from "../logic/sfx";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { useThrottledAnnouncer } from "../logic/LiveAnnouncer";

// Foraging page orchestrator. Owns:
//  * the channel join + teardown,
//  * the local state mirror (plants, skill, session XP, harvest count),
//  * the click → optimistic-hide → harvest-server-call → reconcile flow,
//  * the float / ghost lifecycle (page schedules cleanup, components
//    just animate),
//  * the global keyframes used by the per-plant + ghost + float
//    animations (mounted once at the page level).
//
// Presentation lives in:
//  * `ForagePlant`            — one slot in the orchard
//  * `ForageHarvestGhostImg`  — the pop-and-fade animation on click
//  * `ForageFloatingXpLabel`  — the "+N XP" label that drifts up
//  * `ForageSidePanel`        — the right rail (skill + foraged tally)
//  * `ActivityEvictionPage`   — the swap-in screen when this tab gets evicted

interface Props {
  kind: ForageKind;
  title: string;
  breadcrumb: string | null;
  parentPath: string;
}

const GAME_HEIGHT = 720;

export function ForagePage({ kind, title, breadcrumb, parentPath }: Props) {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const gameRef = useRef<HTMLDivElement | null>(null);
  const { narrow } = useResponsiveLayout();

  const [user, setUser] = useState<api.User | null>(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: GAME_HEIGHT });
  const [plants, setPlants] = useState<Map<string, ForagePlantData>>(new Map());
  const [skill, setSkill] = useState<ForageSkill | null>(null);
  const [frenzy, setFrenzy] = useState<FrenzyStatus | null>(null);
  const [sessionXp, setSessionXp] = useState(0);
  const [count, setCount] = useState(0);
  const [floats, setFloats] = useState<ForageFloatingXp[]>([]);
  const [ghosts, setGhosts] = useState<ForageHarvestGhost[]>([]);
  // Slot ids the player has clicked locally. We hide them in the
  // field while the harvest-pop ghost plays so the user doesn't see
  // a duplicate apple. Cleared once the server sends back the
  // replacement (state: green) for that slot.
  const [claimed, setClaimed] = useState<Set<string>>(new Set());
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [eviction, setEviction] = useState<EvictionReason | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  const harvestRef = useRef<((slotId: string) => void) | null>(null);
  const frenzyRef = useRef<(() => void) | null>(null);

  // Keyboard cursor: id of the ripe plant the keyboard user has
  // selected. Null while the field has no focus / no ripe plants.
  // Arrow keys move it spatially among ripe plants; Enter/Space
  // harvests via the same path as a click.
  const [cursorId, setCursorId] = useState<string | null>(null);
  const announce = useThrottledAnnouncer(1200);

  // ---- size tracking ----
  useEffect(() => {
    if (!gameRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setContainerSize({ w: r.width, h: r.height });
    });
    ro.observe(gameRef.current);
    return () => ro.disconnect();
  }, []);

  // ---- /api/me ----
  useEffect(() => {
    let cancelled = false;
    void api.getMe().then((me) => {
      if (!cancelled) setUser(me.user);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- join the forage channel ----
  useEffect(() => {
    let cancelled = false;
    let leave: (() => void) | null = null;

    void (async () => {
      const me = await api.getMe();
      if (cancelled) return;

      try {
        const { channel, harvest, frenzy } = await joinForage(me.user.id, kind, {
          onState: (s: ForageState) => {
            if (cancelled) return;
            setPlants(new Map(s.slots.map((p) => [p.id, p])));
            setSkill(s.skill);
            setFrenzy(s.frenzy);
          },
          onSlotsChanged: (slots: ForagePlantData[]) => {
            if (cancelled) return;
            setPlants((prev) => {
              const next = new Map(prev);
              for (const slot of slots) next.set(slot.id, slot);
              return next;
            });
          },
          onFrenzy: (status: FrenzyStatus) => {
            if (cancelled) return;
            setFrenzy(status);
          },
          onEvicted: (reason) => {
            if (cancelled) return;
            setEviction(reason);
          },
          onError: (msg) => {
            if (cancelled) return;
            setJoinError(msg);
          },
        });

        harvestRef.current = (slotId: string) => {
          void harvest(slotId).then((res) => {
            if (cancelled) return;
            if (res.ok) {
              setSkill(res.payload.skill);
              setSessionXp((x) => x + res.payload.gained);
              setCount((c) => c + 1);
              if (res.payload.leveled_up) playSfx("ai_level_up");
            } else {
              // Server rejected (inventory full, stale slot, etc.) —
              // un-claim so the plant becomes visible and clickable
              // again. Without this, an inventory-full apple would be
              // hidden forever even though the server still shows it
              // as red.
              setClaimed((prev) => {
                if (!prev.has(slotId)) return prev;
                const next = new Set(prev);
                next.delete(slotId);
                return next;
              });
              showError(res.reason);
            }
          });
        };

        frenzyRef.current = () => {
          // The server pushes the resulting `frenzy` status back over
          // the channel (handled by `onFrenzy`), so the bar updates
          // from the authoritative broadcast — we don't need the reply.
          void frenzy();
        };

        leave = () => {
          channel.leave();
        };
      } catch {
        // join error already surfaced via onError
      }
    })();

    return () => {
      cancelled = true;
      if (leave) leave();
      harvestRef.current = null;
      frenzyRef.current = null;
    };
  }, [kind]);

  // ---- floats cleanup ----
  useEffect(() => {
    if (floats.length === 0) return;
    const t = setTimeout(() => {
      const cutoff = Date.now() - FLOAT_DURATION_MS;
      setFloats((f) => f.filter((x) => x.bornAt > cutoff));
    }, FLOAT_DURATION_MS);
    return () => clearTimeout(t);
  }, [floats]);

  // ---- ghost cleanup (harvest-pop animation) ----
  useEffect(() => {
    if (ghosts.length === 0) return;
    const t = setTimeout(() => {
      const cutoff = Date.now() - HARVEST_POP_MS;
      setGhosts((g) => g.filter((x) => x.bornAt > cutoff));
    }, HARVEST_POP_MS + 20);
    return () => clearTimeout(t);
  }, [ghosts]);

  // ---- claimed cleanup: drop a slot from `claimed` once the server
  // has replied with the fresh green replacement. ----
  useEffect(() => {
    if (claimed.size === 0) return;
    let drop: string[] = [];
    for (const id of claimed) {
      const p = plants.get(id);
      if (!p || p.state === "green") drop.push(id);
    }
    if (drop.length === 0) return;
    setClaimed((prev) => {
      const next = new Set(prev);
      for (const id of drop) next.delete(id);
      return next;
    });
  }, [plants, claimed]);

  // Ripe-plant book-keeping for the keyboard model.
  // - keep `cursorId` pointed at a still-ripe, unclaimed plant
  // - announce ripe-count growth (throttled by useThrottledAnnouncer)
  const ripePlantIds: string[] = [];
  for (const p of plants.values()) {
    if (p.state === "red" && !claimed.has(p.id)) ripePlantIds.push(p.id);
  }
  const ripeCount = ripePlantIds.length;
  const lastRipeCountRef = useRef(0);
  useEffect(() => {
    if (ripeCount > lastRipeCountRef.current) {
      const isApples = kind === "apple_trees";
      const singular = isApples ? "apple" : "strawberry";
      const plural = isApples ? "apples" : "strawberries";
      announce(
        ripeCount === 1 ? `1 ${singular} ripe` : `${ripeCount} ${plural} ripe`,
      );
    }
    lastRipeCountRef.current = ripeCount;
  }, [ripeCount, announce, kind]);

  useEffect(() => {
    if (cursorId == null) return;
    const plant = plants.get(cursorId);
    const stillRipe = plant && plant.state === "red" && !claimed.has(plant.id);
    if (!stillRipe) setCursorId(null);
  }, [plants, claimed, cursorId]);

  const moveCursor = useCallback(
    (dx: number, dy: number) => {
      const ripeList: ForagePlantData[] = [];
      for (const p of plants.values()) {
        if (p.state === "red" && !claimed.has(p.id)) ripeList.push(p);
      }
      if (ripeList.length === 0) return;
      if (cursorId == null || !plants.get(cursorId)) {
        // Land on the ripe plant nearest the field center.
        let best: ForagePlantData = ripeList[0];
        let bestD = Infinity;
        for (const p of ripeList) {
          const d = (p.x_frac - 0.5) ** 2 + (p.y_frac - 0.5) ** 2;
          if (d < bestD) {
            bestD = d;
            best = p;
          }
        }
        setCursorId(best.id);
        return;
      }
      const here = plants.get(cursorId)!;
      // Score candidates: prefer ones whose direction aligns with
      // (dx, dy); tie-break by proximity. Reject directions where the
      // dominant component disagrees (i.e. pressing Right but the
      // candidate is to the left).
      let best: ForagePlantData | null = null;
      let bestScore = Infinity;
      for (const p of ripeList) {
        if (p.id === here.id) continue;
        const ddx = p.x_frac - here.x_frac;
        const ddy = p.y_frac - here.y_frac;
        if (dx !== 0) {
          if (Math.sign(ddx) !== Math.sign(dx)) continue;
          if (Math.abs(ddx) < Math.abs(ddy)) continue;
        } else if (dy !== 0) {
          if (Math.sign(ddy) !== Math.sign(dy)) continue;
          if (Math.abs(ddy) < Math.abs(ddx)) continue;
        }
        const dist = Math.hypot(ddx, ddy);
        if (dist < bestScore) {
          bestScore = dist;
          best = p;
        }
      }
      if (best) setCursorId(best.id);
    },
    [plants, claimed, cursorId],
  );

  const showError = useCallback((reason: HarvestErrorReason) => {
    const msg =
      reason === "inventory_full"
        ? "Inventory is full."
        : reason === "not_ripe"
          ? "Not ripe yet."
          : reason === "gone"
            ? "Already gone."
            : "Couldn't harvest.";
    setErrorBanner(msg);
    setTimeout(() => setErrorBanner(null), 1800);
  }, []);

  const handleClick = useCallback(
    (
      slotIn: ForagePlantData,
      leftIn: number,
      topIn: number,
      cxIn: number,
      cyIn: number,
      clientX: number,
      clientY: number,
    ) => {
      let slot = slotIn;
      let left = leftIn;
      let top = topIn;
      let cx = cxIn;
      let cy = cyIn;

      // If a green wrapper captured the click, the user may actually
      // have been aiming at a red apple sitting at the same pixel —
      // the wrappers are squares but the fruit sprites are roughly
      // circular, so adjacent apples' transparent corners can land
      // over a visible neighbour. Z-index makes red dominate the
      // pixel-stack, but the user's two prior reports of "can't click
      // the red one" say the symptom still surfaces in practice.
      // Belt-and-braces: scan unclaimed reds for any whose bounding
      // box contains the click point, and rewrite the click target to
      // that red plant.
      if (slot.state !== "red" && gameRef.current) {
        const rect = gameRef.current.getBoundingClientRect();
        const px = clientX - rect.left;
        const py = clientY - rect.top;
        let routedRed: ForagePlantData | null = null;
        for (const candidate of plants.values()) {
          if (candidate.state !== "red") continue;
          if (claimed.has(candidate.id)) continue;
          const rLeft = candidate.x_frac * containerSize.w - candidate.size / 2;
          const rTop = candidate.y_frac * containerSize.h - candidate.size / 2;
          if (
            px >= rLeft &&
            px <= rLeft + candidate.size &&
            py >= rTop &&
            py <= rTop + candidate.size
          ) {
            routedRed = candidate;
          }
        }
        if (routedRed) {
          slot = routedRed;
          left = routedRed.x_frac * containerSize.w - routedRed.size / 2;
          top = routedRed.y_frac * containerSize.h - routedRed.size / 2;
          cx = left + routedRed.size / 2;
          cy = top;
        }
      }

      // Pan: map the click's horizontal position within the game
      // area to a stereo pan of -1..1.
      const pan =
        containerSize.w > 0
          ? Math.max(-1, Math.min(1, (cx / containerSize.w) * 2 - 1))
          : 0;

      // Green-fruit clicks (no co-located red): notice sound only.
      if (slot.state !== "red") {
        if (slot.state === "green")
          playSfx("ai_notice_or_warning_sound", { pan });
        return;
      }
      if (!harvestRef.current) return;
      if (claimed.has(slot.id)) return;

      const now = Date.now();
      const xp = KIND_XP[kind];
      // Random pitch ±15% so back-to-back pops don't sound robotic.
      const pitch = 0.85 + Math.random() * 0.3;
      playSfx("ai_pop", { pan, pitch });

      setFloats((f) => [
        ...f,
        { id: `f-${slot.id}-${now}`, x: cx, y: cy, amount: xp, bornAt: now },
      ]);

      // Render a ghost in the same spot doing the harvest-pop
      // animation while the actual slot is hidden via `claimed`. The
      // ghost is a self-contained <img> that the page tears down
      // after HARVEST_POP_MS.
      setGhosts((g) => [
        ...g,
        {
          id: `g-${slot.id}-${now}`,
          left,
          top,
          size: slot.size,
          rotation: slot.rotation,
          kind,
          bornAt: now,
        },
      ]);

      setClaimed((prev) => {
        const next = new Set(prev);
        next.add(slot.id);
        return next;
      });

      harvestRef.current(slot.id);
    },
    [kind, claimed, containerSize.w, containerSize.h, plants],
  );

  const harvestCursor = useCallback(() => {
    const id = cursorId;
    if (id == null) {
      moveCursor(0, 0);
      return;
    }
    const p = plants.get(id);
    if (!p || p.state !== "red") return;
    const left = p.x_frac * containerSize.w - p.size / 2;
    const top = p.y_frac * containerSize.h - p.size / 2;
    const cx = left + p.size / 2;
    const cy = top;
    const rect = gameRef.current?.getBoundingClientRect();
    const clientX = (rect?.left ?? 0) + cx;
    const clientY = (rect?.top ?? 0) + cy;
    handleClick(p, left, top, cx, cy, clientX, clientY);
  }, [cursorId, plants, containerSize.w, containerSize.h, moveCursor, handleClick]);

  const onFieldKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          moveCursor(-1, 0);
          break;
        case "ArrowRight":
          e.preventDefault();
          moveCursor(1, 0);
          break;
        case "ArrowUp":
          e.preventDefault();
          moveCursor(0, -1);
          break;
        case "ArrowDown":
          e.preventDefault();
          moveCursor(0, 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          harvestCursor();
          break;
        default:
          break;
      }
    },
    [moveCursor, harvestCursor],
  );

  if (eviction) {
    return (
      <ActivityEvictionPage
        activityLabel="Foraging"
        reason={eviction}
        title={title}
        breadcrumb={breadcrumb}
        parentPath={parentPath}
        user={user}
      />
    );
  }

  // Bucket plants by state so we can render each bucket inside its
  // own absolutely-positioned overlay container with a fixed z-index
  // (greens < reds < falling). Per-plant z-index then only orders
  // each plant against its same-state neighbours within its bucket.
  // This structural separation is what guarantees a red is never
  // painted behind a green — much more reliable than per-element
  // z-index, which the GPU compositor has historically reordered
  // across siblings whose layer-promotion status differs.
  const greenPlants: ForagePlantData[] = [];
  const redPlants: ForagePlantData[] = [];
  const fallingPlants: ForagePlantData[] = [];
  for (const p of plants.values()) {
    if (p.state === "green") greenPlants.push(p);
    else if (p.state === "red") redPlants.push(p);
    else fallingPlants.push(p);
  }

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
      }}
    >
      {/*
        Global keyframes used by the split-out plant / ghost / float
        components. Mounted once at the page level — children just
        reference these animation names.
      */}
      <style>{`
        @keyframes ripe-pulse {
          0%, 70%, 100% { transform: scale(1); }
          35%           { transform: scale(1.05); }
        }
        @keyframes harvest-pop {
          0%   { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.6) translateY(-30px); opacity: 0; }
        }
        @keyframes apple-fall {
          0%   { transform: translateY(0) rotate(0deg); }
          20%  { transform: translateY(20px) rotate(-8deg); }
          100% { transform: translateY(900px) rotate(120deg); }
        }
        @keyframes forage-float-xp {
          0%   { transform: translate(-50%, 0) scale(1);    opacity: 1; }
          100% { transform: translate(-50%, -54px) scale(1); opacity: 0; }
        }
      `}</style>
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gap: 16,
        }}
      >
        <AppHeader
          user={user}
          backTo={parentPath}
          backLabel="◀ BACK"
        />
        <Breadcrumb text={breadcrumb} />

        {joinError && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${E32.scarlet}`,
              color: E32.scarlet,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
            }}
          >
            {joinError === "in_battle"
              ? "Finish your battle before foraging."
              : `Couldn't enter the foraging area: ${joinError}`}
            <button
              onClick={() => navigate(parentPath)}
              style={{
                marginLeft: 12,
                background: "transparent",
                color: E32.scarlet,
                border: `1px solid ${E32.scarlet}`,
                padding: "2px 8px",
                cursor: "pointer",
                fontFamily: theme.fontDisplay,
                fontSize: 10,
                letterSpacing: theme.letterSpacing,
              }}
            >
              GO BACK
            </button>
          </div>
        )}

        <div
          style={{
            display: "grid",
            // Narrow viewport: stack the side panel below the field.
            // Wide: side-by-side with a 300px sidebar on the right.
            gridTemplateColumns: narrow
              ? "minmax(0, 1fr)"
              : "minmax(0, 1fr) 300px",
            gap: 16,
            alignItems: "start",
          }}
        >
          <section
            ref={gameRef}
            role="application"
            tabIndex={0}
            aria-label={`Foraging field — ${ripeCount} ripe. Use arrow keys to move between ripe plants and Enter to harvest.`}
            onKeyDown={onFieldKeyDown}
            onFocus={() => {
              if (cursorId == null && ripeCount > 0) moveCursor(0, 0);
            }}
            style={{
              position: "relative",
              height: GAME_HEIGHT,
              overflow: "hidden",
              background: E32.forest,
              border: `2px solid ${theme.panelEdgeStrong}`,
            }}
          >
            <div style={{ position: "absolute", inset: 0, zIndex: 100, pointerEvents: "none" }}>
              {greenPlants.map((plant) => (
                <ForagePlant
                  key={plant.id}
                  plant={plant}
                  kind={kind}
                  containerW={containerSize.w}
                  containerH={containerSize.h}
                  hidden={claimed.has(plant.id)}
                  onClick={handleClick}
                />
              ))}
            </div>

            <div style={{ position: "absolute", inset: 0, zIndex: 200, pointerEvents: "none" }}>
              {redPlants.map((plant) => (
                <ForagePlant
                  key={plant.id}
                  plant={plant}
                  kind={kind}
                  containerW={containerSize.w}
                  containerH={containerSize.h}
                  hidden={claimed.has(plant.id)}
                  onClick={handleClick}
                  cursor={cursorId === plant.id}
                />
              ))}
            </div>

            <div style={{ position: "absolute", inset: 0, zIndex: 220, pointerEvents: "none" }}>
              {fallingPlants.map((plant) => (
                <ForagePlant
                  key={plant.id}
                  plant={plant}
                  kind={kind}
                  containerW={containerSize.w}
                  containerH={containerSize.h}
                  hidden={claimed.has(plant.id)}
                  onClick={handleClick}
                />
              ))}
            </div>

            {ghosts.map((g) => (
              <ForageHarvestGhostImg key={g.id} ghost={g} />
            ))}

            {floats.map((f) => (
              <ForageFloatingXpLabel key={f.id} float={f} />
            ))}

            {errorBanner && (
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 300,
                  padding: "6px 14px",
                  background: E32.scarlet,
                  color: E32.parchment,
                  fontFamily: theme.fontDisplay,
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  border: `2px solid ${E32.ink}`,
                }}
              >
                {errorBanner}
              </div>
            )}
          </section>

          <ForageSidePanel
            kind={kind}
            skill={skill}
            sessionXp={sessionXp}
            count={count}
            frenzy={frenzy}
            onFrenzy={() => frenzyRef.current?.()}
          />
        </div>
      </div>
    </div>
  );
}
